/**
 * Remote cluster API.
 *
 * The platform reaches remote clusters with each user's own ssh key, so almost everything
 * here is per-user: which clusters this user may submit to, whether this user's own access
 * still works, and the wizard that establishes it.
 *
 * Two reachability signals are returned separately and must not be merged in the UI:
 * `hostReachable` is what the platform can determine without any credential (a TCP banner
 * probe), while `myAccessOk` is whether *this* user can still authenticate. "The cluster is
 * down" and "your remote account needs re-verifying" call for different actions.
 */
import axiosInstance from './axiosInstance';
import type { Response } from './axiosInstance';

export type ClusterCapability = 'job' | 'calculation' | 'jupyter' | 'terminal' | 'gui';

export type AccountState = 'unverified' | 'verified' | 'failed' | 'revoked';

export interface RemoteAccountView {
    remoteUser: string;
    isDefault: boolean;
    state: AccountState;
    verifiedAt?: string;
}

export interface ClusterView {
    id: string;
    displayName: string;
    description?: string;
    isDefault: boolean;
    remote: boolean;
    transport: 'local' | 'ssh' | 'grpc';
    stagingMode: 'shared' | 'rsync' | 'none';
    capabilities: ClusterCapability[];
    defaultPartition?: string;
    maxWallMinutes?: number;
    sortOrder: number;

    hostReachable: boolean;
    hostCheckedAt?: string;
    hostMessage?: string;
    hostLatencyMs?: number;

    myAccessOk: boolean;
    myAccessState: AccountState;
    myAccessMessage?: string;
    remoteUser?: string;
    verifiedAt?: string;

    accounts?: RemoteAccountView[];
    partitionsSnapshotAt?: string;
    partitionsStale?: boolean;
}

export interface KeyPair {
    privatePath: string;
    publicPath: string;
    publicKey: string;
    generated: boolean;
    /** A key whose permissions were too open and were corrected. ssh silently refuses such a
     *  key and reports "Permission denied (publickey)", which gives the user nothing to act
     *  on, so the fix is reported rather than done quietly. */
    permFixed?: string;
}

export interface KeyCopyResult {
    installed: boolean;
    alreadyPresent: boolean;
    message?: string;
}

export interface VerifyStep {
    name: string;
    ok: boolean;
    skipped?: boolean;
    detail?: string;
    elapsedMs?: number;
}

export interface StagingCandidate {
    path: string;
    exists: boolean;
    writable: boolean;
    freeMb: number;
    fsType: string;
    device: string;
    /** True only when a probe job confirmed the directory is visible on a compute node.
     *  A directory that is not visible would make jobs run against an empty input directory,
     *  and some solvers exit successfully having computed nothing. */
    shared: boolean;
}

export interface VerifyReport {
    ok: boolean;
    hostname?: string;
    remoteUser?: string;
    remoteHome?: string;
    slurmVersion?: string;
    minJobAge?: number;
    hasSbatch: boolean;
    hasSacct: boolean;
    hasSqueue: boolean;
    hasScontrol: boolean;
    hasRsync: boolean;
    hasTar: boolean;
    syncFlavor?: string;
    homeFreeMb?: number;
    candidates?: StagingCandidate[];
    chosenStaging?: string;
    chosenIsShared: boolean;
    partitions?: Record<string, string[]>;
    helperDigest?: string;
    /** Whether a dry-run `sbatch --test` was accepted. False means the remote scheduler
     *  refuses submissions for this account — a QOS or association problem that only the
     *  remote cluster's administrator can fix. The steps table already renders the reason,
     *  so no extra UI is needed to surface it. */
    submitAllowed?: boolean;
    submitBlockReason?: string;
    hasAssociation?: boolean;
    historySources?: string[];
    steps: VerifyStep[];
    message?: string;
}

export interface ConnectedAccount {
    id: number;
    username: string;
    clusterId: string;
    remoteUser: string;
    isDefault: boolean;
    keyPath: string;
    sshConfigHost: string;
    state: AccountState;
    verifiedAt?: string;
    lastError?: string;
    failCount: number;
    syncFlavor?: string;
    remoteHome?: string;
    remoteScratch?: string;
    remoteQuotaMb?: number;
    scratchShared: boolean;
    partitionsSnapAt?: string;
}

/** Clusters this user may submit to. An empty list means multi-cluster support is off. */
export const getClusters = async (): Promise<Response<ClusterView[]> | undefined> => {
    try {
        const r = await axiosInstance.get<Response<ClusterView[]>>('/job/clusters');
        return r.data;
    } catch (e) {
        return undefined;
    }
};

/** Find or create the ssh key the user will authenticate with. */
export const ensureKey = async (generate: boolean): Promise<Response<KeyPair> | undefined> => {
    try {
        const r = await axiosInstance.post<Response<KeyPair>>('/clusters/key', null, {
            params: { generate }
        });
        return r.data;
    } catch (e) {
        return undefined;
    }
};

/**
 * Copy the public key to the remote cluster using a password.
 *
 * The password is sent once, is never stored by the backend, and the endpoint is rate
 * limited: every attempt appears to the remote cluster as coming from this login node's
 * single address, so repeated failures could get the whole platform banned by a remote
 * fail2ban. Surface a `RATE_LIMITED` code as "wait and retry", not as "wrong password".
 */
export const installKey = async (params: {
    clusterId: string;
    remoteUser: string;
    host: string;
    port: number;
    password: string;
    publicKey: string;
}): Promise<Response<KeyCopyResult> | undefined> => {
    try {
        const r = await axiosInstance.post<Response<KeyCopyResult>>('/clusters/install-key', params);
        return r.data;
    } catch (e) {
        return undefined;
    }
};

/** Connect, install the helper, inspect the remote environment and record the result. */
export const verifyCluster = async (params: {
    clusterId: string;
    remoteUser: string;
    keyPath?: string;
    sshConfigHost?: string;
    isDefault?: boolean;
}): Promise<Response<VerifyReport> | undefined> => {
    try {
        const r = await axiosInstance.post<Response<VerifyReport>>('/clusters/verify', params);
        return r.data;
    } catch (e) {
        return undefined;
    }
};

export const listAccounts = async (cluster?: string): Promise<Response<ConnectedAccount[]> | undefined> => {
    try {
        const r = await axiosInstance.get<Response<ConnectedAccount[]>>('/clusters/accounts', {
            params: { cluster }
        });
        return r.data;
    } catch (e) {
        return undefined;
    }
};

/**
 * Disconnect. This removes the platform's record only: the public key stays in the remote
 * authorized_keys, and the response says so, because the platform put it there with the
 * user's password and removing it silently would be worse than leaving it.
 */
export const deleteAccount = async (cluster: string, remoteUser: string): Promise<Response<boolean> | undefined> => {
    try {
        const r = await axiosInstance.delete<Response<boolean>>('/clusters/accounts', {
            params: { cluster, remote_user: remoteUser }
        });
        return r.data;
    } catch (e) {
        return undefined;
    }
};
